"use client";

import { useLocale, useTranslations } from "next-intl";
import { motion } from "motion/react";
import {
    SectionShell,
    EditorialTitle,
    GradientText,
    AnimatedNumber,
    RevealOnScroll,
} from "./_primitives";

export default function StatsSection() {
    const t = useTranslations();
    const locale = useLocale();
    const fmt1 = (v) =>
        v.toLocaleString(locale, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        });

    return (
        <SectionShell label={t("joinInstructor_stats_marker")}>
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-end mb-10 sm:mb-14">
                <RevealOnScroll>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                        {t("joinInstructor_stats_title_part1")}
                        <br />
                        {t("joinInstructor_stats_title_part2")}{" "}
                        <GradientText>{t("joinInstructor_stats_title_part3")}</GradientText>
                    </EditorialTitle>
                </RevealOnScroll>
                <RevealOnScroll delay={0.15}>
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-lg lg:text-right"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinInstructor_stats_subtitle")}
                    </p>
                </RevealOnScroll>
            </div>

            <RevealOnScroll>
                <p className="text-gray-600 dark:text-white/70 max-w-3xl text-base leading-relaxed mb-10">
                    {t("joinInstructor_stats_lead_part1")}{" "}
                    <span className="text-primary font-semibold">
                        {t("joinInstructor_stats_lead_part2")}
                    </span>
                    {t("joinInstructor_stats_lead_part3")}{" "}
                    <span className="text-primary font-semibold">
                        {t("joinInstructor_stats_lead_part4")}
                    </span>{" "}
                    {t("joinInstructor_stats_lead_part5")}
                </p>
            </RevealOnScroll>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Big card: learners */}
                <RevealOnScroll className="col-span-2 lg:row-span-2">
                    <div className="relative group h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gradient-to-br from-gray-50 dark:from-white/[0.04] to-transparent p-8 overflow-hidden">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-primary mb-6">
                            {t("joinInstructor_stats_card_learners_label")}
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span
                                className="text-6xl sm:text-7xl md:text-8xl font-bold text-primary leading-none"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                <AnimatedNumber value={10} format={(v) => Math.round(v).toString()} />
                                K+
                            </span>
                        </div>
                        <p className="text-gray-700 dark:text-white/85 mt-6 text-lg leading-relaxed">
                            {t("joinInstructor_stats_card_learners_desc")}
                        </p>
                        <div className="absolute bottom-6 left-8 right-8 text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 font-mono">
                            {t("joinInstructor_stats_card_learners_footer")}
                        </div>
                    </div>
                </RevealOnScroll>

                {/* Note */}
                <RevealOnScroll delay={0.05}>
                    <StatCard
                        label={t("joinInstructor_stats_card_rating_label")}
                        big={
                            <>
                                <AnimatedNumber
                                    value={4.8}
                                    format={fmt1}
                                />
                                <span className="text-2xl text-gray-500 dark:text-white/55 align-super ml-1">/5</span>
                            </>
                        }
                        desc={t("joinInstructor_stats_card_rating_desc")}
                    />
                </RevealOnScroll>

                {/* Satisfaction (highlight) */}
                <RevealOnScroll delay={0.1}>
                    <div className="relative h-full rounded-2xl p-6 overflow-hidden bg-gradient-to-br from-[#FF7A1A] via-[#FF3B5C] to-[#E51578] text-white">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mb-4">
                            {t("joinInstructor_stats_card_satisfaction_label")}
                        </div>
                        <div
                            className="text-4xl sm:text-5xl md:text-6xl font-bold leading-none"
                            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                        >
                            <AnimatedNumber value={97.9} format={fmt1} />
                            %
                        </div>
                        <p className="text-white/90 mt-4 text-sm leading-relaxed">
                            {t("joinInstructor_stats_card_satisfaction_desc")}
                        </p>
                    </div>
                </RevealOnScroll>

                {/* Audience */}
                <RevealOnScroll delay={0.15}>
                    <StatCard
                        label={t("joinInstructor_stats_card_audience_label")}
                        big={
                            <>
                                <AnimatedNumber
                                    value={200}
                                    format={(v) => Math.round(v).toString()}
                                />
                                K
                            </>
                        }
                        desc={t("joinInstructor_stats_card_audience_desc")}
                    />
                </RevealOnScroll>

                {/* Qualiopi */}
                <RevealOnScroll delay={0.2}>
                    <div className="h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-6 flex flex-col">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/55 mb-4">
                            {t("joinInstructor_stats_card_qualiopi_label")}
                        </div>
                        <div
                            className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary"
                            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                        >
                            Qualiopi
                        </div>
                        <p className="text-gray-500 dark:text-white/55 mt-3 text-sm leading-relaxed">
                            {t("joinInstructor_stats_card_qualiopi_desc")}
                        </p>
                    </div>
                </RevealOnScroll>
            </div>

            {/* Quotes */}
            <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Quote
                    text={t("joinInstructor_stats_quote1_text")}
                    author={t("joinInstructor_stats_quote1_author")}
                />
                <Quote
                    text={t("joinInstructor_stats_quote2_text")}
                    author={t("joinInstructor_stats_quote2_author")}
                />
            </div>
        </SectionShell>
    );
}

function StatCard({ label, big, desc }) {
    return (
        <div className="h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-6 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/55 mb-4">
                {label}
            </div>
            <div
                className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white leading-none"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
                {big}
            </div>
            <p className="text-gray-500 dark:text-white/55 mt-3 text-sm leading-relaxed">{desc}</p>
        </div>
    );
}

function Quote({ text, author }) {
    return (
        <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-6"
        >
            <p
                className="text-gray-700 dark:text-white/85 italic text-base leading-relaxed"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
                « {text} »
            </p>
            <cite className="block mt-4 text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 not-italic">
                {author}
            </cite>
        </motion.blockquote>
    );
}
